import axiosInstance from '../../../axiosConfig';

/**
 * Mirrors the backend's AvailabilityRule schema exactly (openapi.yaml,
 * /api/users/{userId}/availability) - HH:mm local time in the rule's own IANA
 * timezone, not UTC. See BookingEntity/AvailabilityRuleEntity on the backend
 * for why a recurring rule stays local-time while a confirmed booking is UTC.
 *
 * The /api prefix is load-bearing, not cosmetic: Spring Security's CORS
 * resolution (SecurityConfiguration's `http.cors(Customizer.withDefaults())`,
 * no explicit CorsConfigurationSource bean) matches a request's real,
 * registered handler-mapping path - UsersController is mapped under
 * `@RequestMapping("/api")`. A request without the prefix never resolves to
 * that handler's `@CrossOrigin` annotation, so the browser's preflight gets a
 * bare 403 "Invalid CORS request" with no Access-Control-Allow-Origin header
 * - not a 404, which is what made the bug non-obvious: an unauthenticated GET
 * to the wrong path still returns 401 under this app's deny-by-default
 * security chain (confirmed: an arbitrary made-up path returns the identical
 * 401), so "the route exists" cannot be inferred from a 401 alone.
 */
export type DayOfWeek =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export interface AvailabilityRule {
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  slotDurationMinutes: number;
  timezone: string;
}

export interface AvailabilityResponse {
  userId: number;
  rules: AvailabilityRule[];
}

export const getUserAvailability = async (
  userId: number,
): Promise<AvailabilityResponse> => {
  const { data } = await axiosInstance.get<AvailabilityResponse>(
    `/api/users/${userId}/availability`,
  );
  return data;
};

export const setUserAvailability = async (
  userId: number,
  rules: AvailabilityRule[],
): Promise<AvailabilityResponse> => {
  const { data } = await axiosInstance.put<AvailabilityResponse>(
    `/api/users/${userId}/availability`,
    { rules },
  );
  return data;
};
